/**
 * Menu Data
 * 
 * IMPORTANT: All monetary values are stored in ØRE (1/100 of a krone)
 * Example: 16900 øre = 169 kr
 * 
 * This ensures consistency across the entire codebase and avoids
 * floating-point issues when handling money.
 */

export const menuData = {
  starters: [
    {
      id: 'st1',
      name: 'Cæsarsalat',
      description: 'Fersk romanosalat, parmesan, krutonger, cæsardressing',
      price: 12500, // 125 kr
      image: 'https://images.unsplash.com/photo-1550304943-4f24f54ddde9?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'small', nameKey: 'smallSalad', priceModifier: 0 },
          { id: 'large', nameKey: 'largeSalad', priceModifier: 3000 } // +30 kr
        ],
        addIngredients: [
          { id: 'chicken', nameKey: 'grilledChicken', price: 4000 }, // +40 kr
          { id: 'bacon', nameKey: 'bacon', price: 2500 }, // +25 kr
          { id: 'avocado', nameKey: 'avocado', price: 2000 } // +20 kr
        ],
        removeIngredients: [
          { id: 'croutons', nameKey: 'croutons' },
          { id: 'parmesan', nameKey: 'parmesan' }
        ]
      }
    },
    {
      id: 'st2',
      name: 'Mozzarella Sticks',
      description: 'Sprø panert mozzarella med marinara-saus',
      price: 9900, // 99 kr
      image: 'https://images.unsplash.com/photo-1531749668029-2db88e4276c7?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sauces: [
          { id: 'marinara', nameKey: 'marinara', priceModifier: 0 },
          { id: 'ranch', nameKey: 'ranch', priceModifier: 500 }, // +5 kr
          { id: 'garlic-aioli', nameKey: 'garlicAioli', priceModifier: 800 } // +8 kr
        ]
      }
    },
    {
      id: 'st3',
      name: 'Buffalo Wings',
      description: '8 biter med blåmuggost-dipp og selleristenger',
      price: 14900, // 149 kr
      image: 'https://images.unsplash.com/photo-1567620832903-9fc6debc209f?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: '8pcs', nameKey: '8pieces', priceModifier: 0 },
          { id: '12pcs', nameKey: '12pieces', priceModifier: 5000 }, // +50 kr
          { id: '16pcs', nameKey: '16pieces', priceModifier: 10000 } // +100 kr
        ],
        sauces: [
          { id: 'buffalo', nameKey: 'buffalo', priceModifier: 0 },
          { id: 'bbq', nameKey: 'bbqSauce', priceModifier: 0 },
          { id: 'honey-mustard', nameKey: 'honeyMustard', priceModifier: 500 }, // +5 kr
          { id: 'teriyaki', nameKey: 'teriyaki', priceModifier: 800 } // +8 kr
        ]
      }
    },
    {
      id: 'st4',
      name: 'Hvitløksbrød',
      description: 'Ristet brød med hvitløkssmør og urter',
      price: 6900, // 69 kr
      image: 'https://images.unsplash.com/photo-1509722747041-616f39b57569?auto=format&fit=crop&w=800&q=80',
      customizations: {
        cheese: [
          { id: 'no-cheese', nameKey: 'noCheese', priceModifier: 0 },
          { id: 'mozzarella', nameKey: 'mozzarella', priceModifier: 1500 }, // +15 kr
          { id: 'parmesan', nameKey: 'parmesan', priceModifier: 2000 } // +20 kr
        ]
      }
    }
  ],
  burgers: [
    {
      id: 'bg1',
      name: 'Klassisk Cheeseburger',
      description: 'Storfekjøtt, cheddar, salat, tomat, løk, sylteagurk',
      price: 16900, // 169 kr
      image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=800&q=80',
      customizations: {
        cheese: [
          { id: 'cheddar', nameKey: 'cheddar', priceModifier: 0 },
          { id: 'swiss', nameKey: 'swiss', priceModifier: 500 }, // +5 kr
          { id: 'blue-cheese', nameKey: 'blueCheese', priceModifier: 1000 }, // +10 kr
          { id: 'goat-cheese', nameKey: 'goatCheese', priceModifier: 1500 } // +15 kr
        ],
        sauces: [
          { id: 'ketchup', nameKey: 'ketchup', priceModifier: 0 },
          { id: 'mayo', nameKey: 'mayo', priceModifier: 0 },
          { id: 'mustard', nameKey: 'mustard', priceModifier: 0 },
          { id: 'aioli', nameKey: 'aioli', priceModifier: 500 } // +5 kr
        ],
        addIngredients: [
          { id: 'bacon', nameKey: 'bacon', price: 2500 }, // +25 kr
          { id: 'avocado', nameKey: 'avocado', price: 2000 }, // +20 kr
          { id: 'mushrooms', nameKey: 'mushrooms', price: 1500 }, // +15 kr
          { id: 'jalapenos', nameKey: 'jalapenos', price: 1000 } // +10 kr
        ],
        removeIngredients: [
          { id: 'lettuce', nameKey: 'lettuce' },
          { id: 'tomato', nameKey: 'tomato' },
          { id: 'onion', nameKey: 'onion' },
          { id: 'pickles', nameKey: 'pickles' }
        ]
      }
    },
    {
      id: 'bg2',
      name: 'BBQ Bacon Burger',
      description: 'Storfekjøtt, bacon, BBQ-saus, løkringer, salat',
      price: 19900, // 199 kr
      image: 'https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5?auto=format&fit=crop&w=800&q=80',
      customizations: {
        cheese: [
          { id: 'cheddar', nameKey: 'cheddar', priceModifier: 0 },
          { id: 'swiss', nameKey: 'swiss', priceModifier: 500 }, // +5 kr
          { id: 'smoked-gouda', nameKey: 'smokedGouda', priceModifier: 1000 } // +10 kr
        ],
        sauces: [
          { id: 'bbq', nameKey: 'bbqSauce', priceModifier: 0 },
          { id: 'chipotle-mayo', nameKey: 'chipotleMayo', priceModifier: 500 }, // +5 kr
          { id: 'sriracha-aioli', nameKey: 'srirachaAioli', priceModifier: 800 } // +8 kr
        ],
        addIngredients: [
          { id: 'extra-bacon', nameKey: 'extraBacon', price: 2500 }, // +25 kr
          { id: 'fried-egg', nameKey: 'friedEgg', price: 1500 }, // +15 kr
          { id: 'avocado', nameKey: 'avocado', price: 2000 } // +20 kr
        ]
      }
    },
    {
      id: 'bg3',
      name: 'Vegetarburger',
      description: 'Plantebasert burger, avokado, spirer, tomat, vegan majones',
      price: 15900, // 159 kr
      image: 'https://images.unsplash.com/photo-1550547660-d9450f859349?auto=format&fit=crop&w=800&q=80',
      customizations: {
        cheese: [
          { id: 'no-cheese', nameKey: 'noCheese', priceModifier: 0 },
          { id: 'vegan-cheese', nameKey: 'veganCheese', priceModifier: 1500 }, // +15 kr
          { id: 'regular-cheese', nameKey: 'regularCheese', priceModifier: 1000 } // +10 kr
        ],
        sauces: [
          { id: 'vegan-mayo', nameKey: 'veganMayo', priceModifier: 0 },
          { id: 'hummus', nameKey: 'hummus', priceModifier: 500 }, // +5 kr
          { id: 'tahini', nameKey: 'tahini', priceModifier: 800 } // +8 kr
        ],
        addIngredients: [
          { id: 'extra-avocado', nameKey: 'extraAvocado', price: 2000 }, // +20 kr
          { id: 'grilled-peppers', nameKey: 'grilledPeppers', price: 1500 }, // +15 kr
          { id: 'cucumber', nameKey: 'cucumber', price: 1000 } // +10 kr
        ]
      }
    },
    {
      id: 'bg4',
      name: 'Dobbel Deluxe',
      description: 'To burgere av storfekjøtt, dobbel ost, spesialsaus, salat, løk',
      price: 22900, // 229 kr
      image: 'https://images.unsplash.com/photo-1582196016295-f8c8bd4b3a99?auto=format&fit=crop&w=800&q=80',
      customizations: {
        cheese: [
          { id: 'double-cheddar', nameKey: 'doubleCheddar', priceModifier: 0 },
          { id: 'mixed-cheese', nameKey: 'mixedCheese', priceModifier: 1000 }, // +10 kr
          { id: 'triple-cheese', nameKey: 'tripleCheese', priceModifier: 2000 } // +20 kr
        ],
        addIngredients: [
          { id: 'triple-patty', nameKey: 'triplePatty', price: 5000 }, // +50 kr
          { id: 'bacon', nameKey: 'bacon', price: 2500 }, // +25 kr
          { id: 'onion-rings', nameKey: 'onionRings', price: 2000 } // +20 kr
        ]
      }
    }
  ],
  mains: [
    {
      id: 'mn1',
      name: 'Grillet Laks',
      description: 'Fersk laks med sitron-urtesmør og sesongens grønnsaker',
      price: 24900, // 249 kr
      image: 'https://images.unsplash.com/photo-1485704686097-ed47f7263ca4?auto=format&fit=crop&w=800&q=80',
    },
    {
      id: 'mn2',
      name: 'Kylling Parmesan',
      description: 'Panert kyllingbryst, marinara-saus, mozzarella, pasta',
      price: 21900, // 219 kr
      image: 'https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'mn3',
      name: 'Ribeye Biff',
      description: '340g ribeye, potetmos, grillet asparges',
      price: 32900, // 329 kr
      image: 'https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'mn4',
      name: 'Fish & Chips',
      description: 'Frityrstekt torsk, pommes frites, coleslaw, tartarsaus',
      price: 18900, // 189 kr
      image: 'https://images.unsplash.com/photo-1579208575657-c595a05383b7?auto=format&fit=crop&w=800&q=80'
    }
  ],
  desserts: [
    {
      id: 'ds1',
      name: 'Sjokoladekake',
      description: 'Rik sjokoladekake med sjokoladeganache',
      price: 8900, // 89 kr
      image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'ds2',
      name: 'Ostekake',
      description: 'Ostekake i New York-stil med bærkompott',
      price: 7900, // 79 kr
      image: 'https://images.unsplash.com/photo-1533134242443-d4fd215305ad?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'ds3',
      name: 'Iskrem Sundae',
      description: 'Vaniljeis, sjokoladesaus, pisket krem, kirsebær',
      price: 6900, // 69 kr
      image: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'ds4',
      name: 'Eplekake',
      description: 'Hjemmelaget eplekake med vaniljeis',
      price: 7400, // 74 kr
      image: 'https://images.unsplash.com/photo-1568571780765-9276ac8b75a2?auto=format&fit=crop&w=800&q=80'
    }
  ],
  sodas: [
    {
      id: 'sd1',
      name: 'Coca Cola',
      description: 'Klassisk Cola',
      price: 2900, // 29 kr
      image: 'https://images.unsplash.com/photo-1622483767028-3f66f32aef97?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'small', nameKey: 'smallGlass', priceModifier: 0 },
          { id: 'medium', nameKey: 'mediumGlass', priceModifier: 1000 }, // +10 kr
          { id: 'large', nameKey: 'largeGlass', priceModifier: 2000 } // +20 kr
        ]
      }
    },
    {
      id: 'sd2',
      name: 'Pepsi',
      description: 'Pepsi Cola',
      price: 2900, // 29 kr
      image: 'https://images.unsplash.com/photo-1629203851122-3726ecdf080e?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'small', nameKey: 'smallGlass', priceModifier: 0 },
          { id: 'medium', nameKey: 'mediumGlass', priceModifier: 1000 }, // +10 kr
          { id: 'large', nameKey: 'largeGlass', priceModifier: 2000 } // +20 kr
        ]
      }
    },
    {
      id: 'sd3',
      name: 'Sprite',
      description: 'Sitron-lime brus',
      price: 2900, // 29 kr
      image: 'https://images.unsplash.com/photo-1625772299848-391b6a87d7b3?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'small', nameKey: 'smallGlass', priceModifier: 0 },
          { id: 'medium', nameKey: 'mediumGlass', priceModifier: 1000 }, // +10 kr
          { id: 'large', nameKey: 'largeGlass', priceModifier: 2000 } // +20 kr
        ],
        addIngredients: [
          { id: 'extra-lime', nameKey: 'extraLime', price: 500 }, // +5 kr
          { id: 'fresh-mint', nameKey: 'freshMint', price: 800 } // +8 kr
        ]
      }
    },
    {
      id: 'sd4',
      name: 'Appelsinjuice',
      description: 'Ferskpresset appelsinjuice',
      price: 4900, // 49 kr
      image: 'https://images.unsplash.com/photo-1613478223719-2ab802602423?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'small', nameKey: 'smallGlass', priceModifier: 0 },
          { id: 'large', nameKey: 'largeGlass', priceModifier: 2000 } // +20 kr
        ],
        addIngredients: [
          { id: 'ice', nameKey: 'ice', price: 0 },
          { id: 'fresh-mint', nameKey: 'freshMint', price: 800 } // +8 kr
        ]
      }
    }
  ],
  beers: [
    {
      id: 'br1',
      name: 'Heineken',
      description: 'Premium lagerøl',
      price: 5900, // 59 kr
      image: 'https://images.unsplash.com/photo-1618885472179-5e474019f2a9?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'bottle', nameKey: 'bottle330ml', priceModifier: 0 },
          { id: 'pint', nameKey: 'pint500ml', priceModifier: 2500 } // +25 kr
        ]
      }
    },
    {
      id: 'br2',
      name: 'Corona Extra',
      description: 'Meksikansk øl',
      price: 6400, // 64 kr
      image: 'https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'bottle', nameKey: 'bottle330ml', priceModifier: 0 }
        ],
        addIngredients: [
          { id: 'lime-wedge', nameKey: 'limeWedge', price: 500 }, // +5 kr
          { id: 'extra-lime', nameKey: 'extraLime', price: 1000 } // +10 kr
        ]
      }
    },
    {
      id: 'br3',
      name: 'Guinness',
      description: 'Irsk dry stout',
      price: 6900, // 69 kr
      image: 'https://images.unsplash.com/photo-1518176258769-f227c798150e?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'can', nameKey: 'can440ml', priceModifier: 0 },
          { id: 'pint', nameKey: 'pint500ml', priceModifier: 2000 } // +20 kr
        ]
      }
    },
    {
      id: 'br4',
      name: 'Lokal IPA',
      description: 'Håndverksøl India Pale Ale',
      price: 7900, // 79 kr
      image: 'https://images.unsplash.com/photo-1571613316887-6f8d5cbf7ef7?auto=format&fit=crop&w=800&q=80',
      customizations: {
        sizes: [
          { id: 'bottle', nameKey: 'bottle330ml', priceModifier: 0 },
          { id: 'pint', nameKey: 'pint500ml', priceModifier: 3000 } // +30 kr
        ]
      }
    }
  ]
}

export const categoryNames = {
  starters: 'Forretter',
  burgers: 'Burgere', 
  mains: 'Hovedretter',
  desserts: 'Desserter',
  sodas: 'Brus',
  beers: 'Øl'
}
